import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import Layout from '../components/Layout';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const steps = ['Scan QR Code', 'Verify Code', 'Complete'];

const TwoFactorSetup = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const { user, logout } = useAuth();

  useEffect(() => {
    if (user?.two_factor_enabled) {
      setActiveStep(3);
    }
  }, [user]);

  const handleSetup = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.post('/auth/setup-2fa');
      setQrCodeUrl(response.data.qr_code_url);
      setSecret(response.data.secret);
      setActiveStep(1);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to setup 2FA');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await axios.post('/auth/verify-2fa', { code: verificationCode });
      setSuccess(true);
      setActiveStep(2);
      // Reload user to get updated 2FA status
      window.location.reload();
    } catch (error) {
      setError(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async () => {
    if (window.confirm('Are you sure you want to disable two-factor authentication?')) {
      setLoading(true);
      setError('');
      try {
        await axios.post('/auth/disable-2fa');
        setSuccess(true);
        window.location.reload();
      } catch (error) {
        setError(error.response?.data?.detail || 'Failed to disable 2FA');
      } finally {
        setLoading(false);
      }
    }
  };

  if (user?.two_factor_enabled && activeStep < 3) {
    return (
      <Layout>
        <Container maxWidth={false} sx={{ width: '100%' }}>
          <Paper sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom>
              Two-Factor Authentication
            </Typography>
            <Alert severity="success" sx={{ mb: 3 }}>
              Two-factor authentication is currently enabled for your account.
            </Alert>
            <Button variant="outlined" color="error" onClick={handleDisable} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : 'Disable 2FA'}
            </Button>
          </Paper>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth={false} sx={{ width: '100%' }}>
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Two-Factor Authentication Setup
          </Typography>

          <Stepper activeStep={activeStep} sx={{ mt: 4, mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {success && activeStep === 2 && (
            <Alert severity="success" sx={{ mb: 3 }}>
              Two-factor authentication has been enabled successfully!
            </Alert>
          )}

          {activeStep === 0 && (
            <Box>
              <Typography variant="body1" paragraph>
                Two-factor authentication adds an extra layer of security to your account.
                You'll need to use an authenticator app (like Google Authenticator or Authy)
                to generate codes when logging in.
              </Typography>
              <Button
                variant="contained"
                onClick={handleSetup}
                disabled={loading}
                fullWidth
                sx={{ mt: 2 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Start Setup'}
              </Button>
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <Typography variant="body1" paragraph>
                1. Install an authenticator app on your phone (Google Authenticator, Authy, etc.)
              </Typography>
              <Typography variant="body1" paragraph>
                2. Scan this QR code with your authenticator app:
              </Typography>
              <Box display="flex" justifyContent="center" my={3}>
                {qrCodeUrl && (
                  <img src={qrCodeUrl} alt="QR Code" style={{ maxWidth: '300px' }} />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                Or enter this secret manually: <code>{secret}</code>
              </Typography>
              <Typography variant="body1" paragraph>
                3. Enter the 6-digit code from your authenticator app:
              </Typography>
              <TextField
                fullWidth
                label="Verification Code"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ maxLength: 6 }}
                sx={{ mb: 2 }}
              />
              <Button
                variant="contained"
                onClick={handleVerify}
                disabled={loading || verificationCode.length !== 6}
                fullWidth
              >
                {loading ? <CircularProgress size={24} /> : 'Verify and Enable'}
              </Button>
            </Box>
          )}

          {activeStep === 2 && success && (
            <Box textAlign="center">
              <Typography variant="h6" color="success.main" gutterBottom>
                Setup Complete!
              </Typography>
              <Typography variant="body1" paragraph>
                Two-factor authentication is now enabled. You'll need to enter a code from
                your authenticator app each time you log in.
              </Typography>
            </Box>
          )}
        </Paper>
      </Container>
    </Layout>
  );
};

export default TwoFactorSetup;

